// Quick Stats: one printable sheet of team and individual stats, built from
// the legacy boxscore JSON (FootballService.getBoxscoreJson) so the sheet
// shows the same numbers the boxscore push sends. Layout ported from
// live-stats-ui footballReports.js generateCoachsView.

export const ALL = "all";

export const PLAYER_CATEGORIES = [
	{ key: "passing", label: "Passing", max: 3 },
	{ key: "rushing", label: "Rushing", max: 3 },
	{ key: "receiving", label: "Receiving", max: 6 },
	{ key: "tackling", label: "Tackling", max: 6 },
	{ key: "fieldGoals", label: "Field Goals", max: 3 },
	{ key: "punting", label: "Punting", max: 3 },
	{ key: "puntReturns", label: "Punt Returns", max: 3 },
	{ key: "kickReturns", label: "Kick Returns", max: 3 }
];

export const TEAM_STAT_TOGGLES = [
	{ key: "passAtt", label: "Pass attempts" },
	{ key: "passComp", label: "Pass completions" },
	{ key: "passPct", label: "Pass percentage" },
	{ key: "fumbleReturns", label: "Fumble returns and yards" },
	{ key: "puntReturns", label: "Punt returns and yards" },
	{ key: "kickReturns", label: "Kickoff returns and yards" },
	{ key: "intReturns", label: "Interception returns and yards" },
	{ key: "punts", label: "Punt total and average" },
	{ key: "fumbles", label: "Fumbles and fumbles lost" },
	{ key: "penalties", label: "Penalties and penalty yards" },
	{ key: "possession", label: "Possession time" },
	{ key: "thirdDown", label: "Third-down conversions" },
	{ key: "fourthDown", label: "Fourth-down conversions" }
];

export const DEFAULT_OPTIONS = {
	limits: Object.fromEntries(
		PLAYER_CATEGORIES.map(({ key, max }) => [key, max])
	),
	teamStats: Object.fromEntries(TEAM_STAT_TOGGLES.map(({ key }) => [key, true]))
};

// Legacy PAT codes for a successful try (see core ScoringSerializer _getPatProps)
const PAT_GOOD_CODES = [16, 17, 18, 24];
const PAT_DROP_KICK_GOOD = 24;

const HOW_LABELS = {
	pass: "PASS",
	rush: "RUSH",
	"rushing touchdown": "RUSH",
	"interception return": "INT RETURN",
	"fumble recovered": "FUMBLE RETURN",
	"punt return": "PUNT RETURN",
	"kick off return": "KICK RETURN",
	"field goal return": "FG RETURN",
	"bar ball return": "RETURN",
	"rebound net return": "RETURN"
};

const int = (v) => parseInt(v, 10) || 0;
const num = (v) => parseFloat(v) || 0;

const mmss = (seconds) => {
	const s = int(seconds);
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const periodLabel = (n) =>
	int(n) > 4 ? `OT${int(n) > 5 ? int(n) - 4 : ""}` : `${n}`;

// Pass completions/attempts toggles drop their part from the combined
// COMP-ATT-INT line, on the team row and the player Passing column alike
const passParts = (show, [comp, att, interceptions]) =>
	[
		show.passComp && [comp, "passComp"],
		show.passAtt && [att, "passAtt"],
		[interceptions, "passInt"]
	].filter(Boolean);
const passLabel = (parts) => parts.map(([label]) => label).join("-");
const passValue = (parts, t) => parts.map(([, key]) => int(t[key])).join("-");

const teamRows = (away, home, show, hasDeuces) => {
	const both = (fn) => [fn(away), fn(home)];
	const teamPass = passParts(show, ["COMP", "ATT", "INT"]);
	const pct = (t) =>
		int(t.passAtt)
			? `${((int(t.passComp) / int(t.passAtt)) * 100).toFixed(1)}%`
			: "-";
	const noYds = (no, yds) => (t) => `${int(t[no])}-${int(t[yds])}`;
	const sacks = (t) => int(t.defenseSackUa) + num(t.defenseSackA) / 2;

	return [
		["FIRST DOWNS", true, both((t) => int(t.firstDownNo))],
		[
			"3RD DOWN",
			show.thirdDown,
			both((t) => `${int(t.conversionThirdConv)}/${int(t.conversionThirdAtt)}`)
		],
		[
			"4TH DOWN",
			show.fourthDown,
			both((t) => `${int(t.conversionFourthConv)}/${int(t.conversionFourthAtt)}`)
		],
		[
			"TOTAL OFFENSE (YDS-PLAYS)",
			true,
			both(noYds("totalOffYards", "totalOffPlays"))
		],
		["NET PASSING", true, both((t) => int(t.passYards))],
		[passLabel(teamPass), true, both((t) => passValue(teamPass, t))],
		["COMP %", show.passPct, both(pct)],
		["NET RUSHING (YDS-ATT)", true, both(noYds("rushYards", "rushAtt"))],
		[
			"PENALTIES (NO-YDS)",
			show.penalties,
			both(noYds("penaltyNo", "penaltyYards"))
		],
		["FUMBLES (NO-LOST)", show.fumbles, both(noYds("fumblesNo", "fumblesLost"))],
		["SACKS BY", true, both(sacks)],
		[
			"PUNTS (NO-AVG)",
			show.punts,
			both((t) => `${int(t.kickPuntNo)}-${num(t.kickPuntAvg).toFixed(1)}`)
		],
		[
			"PUNT RETURNS (NO-YDS)",
			show.puntReturns,
			both(noYds("returnsPuntNo", "returnsPuntYards"))
		],
		[
			"KICKOFF RETURNS (NO-YDS)",
			show.kickReturns,
			both(noYds("returnsKickNo", "returnsKickYards"))
		],
		[
			"INT RETURNS (NO-YDS)",
			show.intReturns,
			both(noYds("returnsIntNo", "returnsIntYards"))
		],
		[
			"FUMBLE RETURNS (NO-YDS)",
			show.fumbleReturns,
			both(noYds("returnsFumbNo", "returnsFumbYards"))
		],
		["DEUCES", hasDeuces, both((t) => int(t.kickKoDeuce))],
		["TIME OF POSSESSION", show.possession, both((t) => mmss(t.miscPossession))]
	]
		.filter(([, visible]) => visible)
		.map(([label, , [awayValue, homeValue]]) => ({
			label,
			away: awayValue,
			home: homeValue
		}));
};

const playerCategories = (eventInfo, show) => {
	const playerPass = passParts(show, ["C", "A", "I"]);
	const fgValue = int(eventInfo.fieldGoal) || 3;
	const patValue = int(eventInfo.kickPat) || 1;
	const dropFgValue = int(eventInfo.dropKickFieldGoal) || fgValue;
	const dropPatValue = int(eventInfo.dropKickPAT) || patValue;
	const kickPoints = (p) =>
		(int(p.kickFgMad) - int(p.dropKickFieldGoal)) * fgValue +
		int(p.dropKickFieldGoal) * dropFgValue +
		(int(p.epOffKickMd) - int(p.dropKickPAT)) * patValue +
		int(p.dropKickPAT) * dropPatValue;
	const tackles = (p) => int(p.defenseTackUa) + int(p.defenseTackA);
	const by = (fn) => (a, b) => fn(b) - fn(a);
	const returns = (prefix) => ({
		cols: ["Player", "NO", "YDS", "TD", "LONG"],
		filter: (p) => int(p[`${prefix}No`]) > 0,
		sort: by((p) => int(p[`${prefix}Yards`])),
		row: (p) => [
			int(p[`${prefix}No`]),
			int(p[`${prefix}Yards`]),
			int(p[`${prefix}Td`]),
			int(p[`${prefix}Long`])
		]
	});

	return {
		passing: {
			title: "PASSING",
			cols: ["Player", passLabel(playerPass), "YDS", "TD"],
			filter: (p) => int(p.passAtt) > 0,
			sort: by((p) => int(p.passYards)),
			row: (p) => [passValue(playerPass, p), int(p.passYards), int(p.passTd)]
		},
		rushing: {
			title: "RUSHING",
			cols: ["Player", "ATT", "YDS", "TD"],
			filter: (p) => int(p.rushAtt) > 0,
			sort: by((p) => int(p.rushYards)),
			row: (p) => [int(p.rushAtt), int(p.rushYards), int(p.rushTd)]
		},
		receiving: {
			title: "RECEIVING",
			cols: ["Player", "NO", "YDS", "TD"],
			filter: (p) => int(p.receivingNo) > 0,
			sort: by((p) => int(p.receivingYards)),
			row: (p) => [int(p.receivingNo), int(p.receivingYards), int(p.receivingTd)]
		},
		tackling: {
			title: "TACKLING",
			cols: ["Player", "SOLO-AST", "SCK", "INT"],
			filter: (p) => tackles(p) > 0,
			sort: by(tackles),
			row: (p) => [
				`${int(p.defenseTackUa)}-${int(p.defenseTackA)}`,
				num(p.defenseTotalSack) || "-",
				int(p.returnsIntNo) || "-"
			]
		},
		fieldGoals: {
			title: "FIELD GOALS",
			cols: ["Player", "FG", "XP", "PTS"],
			filter: (p) => int(p.kickFgAtt) > 0 || int(p.epOffKickAt) > 0,
			sort: by((p) => int(p.kickFgMad)),
			row: (p) => [
				`${int(p.kickFgMad)}/${int(p.kickFgAtt)}`,
				`${int(p.epOffKickMd)}/${int(p.epOffKickAt)}`,
				kickPoints(p)
			]
		},
		punting: {
			title: "PUNTING",
			cols: ["Player", "NO", "YDS", "AVG", "LONG"],
			filter: (p) => int(p.kickPuntNo) > 0,
			sort: by((p) => int(p.kickPuntYards)),
			row: (p) => [
				int(p.kickPuntNo),
				int(p.kickPuntYards),
				(int(p.kickPuntYards) / int(p.kickPuntNo)).toFixed(1),
				int(p.kickPuntLong)
			]
		},
		puntReturns: { title: "PUNT RETURNS", ...returns("returnsPunt") },
		kickReturns: { title: "KICK RETURNS", ...returns("returnsKick") }
	};
};

const playerSections = (away, home, eventInfo, limits, show) => {
	const categories = playerCategories(eventInfo, show);
	const pick = (team, cat, limit) => {
		const players = (team.players || []).filter(cat.filter).sort(cat.sort);
		return (limit === ALL ? players : players.slice(0, int(limit))).map((p) => [
			p.completeName,
			...cat.row(p)
		]);
	};

	return PLAYER_CATEGORIES.map(({ key }) => {
		const cat = categories[key];
		const limit = limits[key];
		return {
			key,
			title: cat.title,
			cols: cat.cols,
			away: pick(away, cat, limit),
			home: pick(home, cat, limit)
		};
	}).filter((section) => section.away.length > 0 || section.home.length > 0);
};

// The serialized score is read before the try, so add a good PAT. `patPoints`
// is the configured kick value even for a drop kick, which is worth
// `dropKickPAT` (indoor)
const patPointsScored = (score, eventInfo) => {
	if (!PAT_GOOD_CODES.includes(score.patCode)) {
		return 0;
	}
	return score.patCode === PAT_DROP_KICK_GOOD
		? int(eventInfo.dropKickPAT) || int(score.patPoints)
		: int(score.patPoints);
};

const scoringRows = (scoring, away, home, eventInfo) => {
	const nameOf = (team, uniform) =>
		uniform !== undefined && uniform !== null
			? (team.players || []).find((p) => `${p.uniform}` === `${uniform}`)
					?.completeName
			: undefined;

	return (scoring || []).map((score) => {
		const team = score.homeTeam ? home : away;
		const pat = patPointsScored(score, eventInfo);
		const scorer = nameOf(team, score.scorer);
		const how = HOW_LABELS[score.how];
		return {
			description: `${team.name} - ${score.type}${scorer ? ` ${scorer}` : ""}${
				how ? ` (${how})` : ""
			}`,
			period: periodLabel(score.quarter),
			clock: `${String(int(score.mins)).padStart(2, "0")}:${String(
				int(score.secs)
			).padStart(2, "0")}`,
			score: `${int(score.visitorScore) + (score.homeTeam ? 0 : pat)}-${
				int(score.homeScore) + (score.homeTeam ? pat : 0)
			}`
		};
	});
};

export const buildQuickStats = (
	boxscoreJson,
	lineScore = [],
	options = DEFAULT_OPTIONS
) => {
	const { eventInfo = {}, teams = [] } = boxscoreJson || {};
	const [away = {}, home = {}] = teams;
	const hasDeuces = int(away.kickKoDeuce) + int(home.kickKoDeuce) > 0;
	// Periods not reached yet print "-" rather than a 0 nobody scored
	const played = (p) =>
		p.isTotal ||
		eventInfo.statusCode === "FINAL" ||
		int(p.number) <= int(eventInfo.statusPeriod);

	return {
		title: "QUICK STATS",
		header: {
			awayName: away.name || "Away",
			homeName: home.name || "Home"
		},
		lineScore: {
			periods: lineScore
				.filter((p) => !p.isTotal)
				.map((p) => periodLabel(p.number)),
			away: lineScore.map((p) => (played(p) ? int(p.awayScore) : "-")),
			home: lineScore.map((p) => (played(p) ? int(p.homeScore) : "-"))
		},
		teamRows: teamRows(away, home, options.teamStats, hasDeuces),
		playerSections: playerSections(
			away,
			home,
			eventInfo,
			options.limits,
			options.teamStats
		),
		scoring: scoringRows(eventInfo.scoring, away, home, eventInfo)
	};
};
